import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import api from '@/admin/lib/api'

// ── Types ────────────────────────────────────────────────

/** What the voucher endpoints let staff do; the server lists what applies. */
export type VoucherAction =
  'issue' | 'resend' | 'number' | 'cancel' | 'reactivate' | 'redeem'

export interface ClaimVoucher {
  id: string
  number: string | null
  status: string
  state: { key: string; label: string; color: string }
  program: { key: string; label: string }
  claim: { id: string; reference_number: string }
  payment: { id: string; processable: boolean }
  /** Null unless the caller may see claim decisions. */
  amount: string | null
  /**
   * WPS-244: the credit / reference its payment settled under and who recorded
   * it. Null unless the caller may see claim decisions.
   */
  payment_reference?: {
    number: string | null
    origin: string
    /** Who typed the reference in, and when; null when it came from a payment system. */
    entered_by?: string | null
    entered_at?: string | null
  } | null
  /** When the claim had closed, for a voucher redeemed after it. */
  claim_closed_at?: string | null
  expires_on: string | null
  issued_at: string | null
  redeemed_at: string | null
  confirmation_number: string | null
  redeemer: { organisation_id: string | null; name: string } | null
  actions: VoucherAction[]
  created_at?: string
}

/** A field the tenant adds to its vouchers, captured at redemption. */
export interface VoucherField {
  code_name: string
  display_name: string
  field_type: string
  required: boolean
  configuration_data: Record<string, unknown>
  order: number
}

export interface AwaitingVoucherPayment {
  payment_id: string
  amount: string | null
  program: { key: string; label: string } | null
  actions: 'mint'[]
}

export interface ClaimVouchers {
  vouchers: ClaimVoucher[]
  awaiting: AwaitingVoucherPayment[]
  fields?: VoucherField[]
}

export interface VoucherProgram {
  id: string
  key: string
  label: string
  is_active: boolean
  payment_type: string
  number_source: string
  valid_for_days: number | null
}

export interface VoucherPrograms {
  programs: VoucherProgram[]
  number_sources: { value: string; label: string }[]
}

// ── Claim vouchers ───────────────────────────────────────
// Mutations refresh on error too: a refused redeem can expire the voucher.

export function useClaimVouchers(claimUuid: string, enabled = true) {
  return useQuery<ClaimVouchers>({
    queryKey: ['claim', claimUuid, 'vouchers'],
    queryFn: () =>
      api
        .get(`/claims/claims/${claimUuid}/vouchers/`)
        .then((r) => r.data as ClaimVouchers),
    enabled: enabled && !!claimUuid,
  })
}

export interface VoucherActionInput {
  voucherId: string
  action: Exclude<VoucherAction, 'redeem'>
  body?: Record<string, unknown>
}

export function useVoucherAction(claimUuid: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ voucherId, action, body }: VoucherActionInput) =>
      api
        .post(
          `/claims/claims/${claimUuid}/vouchers/${voucherId}/${action}/`,
          body ?? {},
        )
        .then((r) => r.data as ClaimVoucher),
    onSettled: () => qc.invalidateQueries({ queryKey: ['claim', claimUuid] }),
  })
}

export function useMintVoucher(claimUuid: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (paymentId: string) =>
      api
        .post(`/claims/claims/${claimUuid}/payments/${paymentId}/voucher/`, {})
        .then((r) => r.data as ClaimVoucher),
    onSettled: () => qc.invalidateQueries({ queryKey: ['claim', claimUuid] }),
  })
}

export interface RedeemVoucherInput {
  number: string
  claim_number: string
  redeemer: { name: string }
  attributes?: Record<string, unknown>
}

export function useRedeemVoucher(claimUuid: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: RedeemVoucherInput) =>
      api
        .post('/claims/vouchers/redeem/', body)
        .then((r) => r.data as ClaimVoucher),
    onSettled: () => qc.invalidateQueries({ queryKey: ['claim', claimUuid] }),
  })
}

// ── Programs (Settings > Claims > Vouchers) ──────────────

export function useVoucherPrograms(enabled = true) {
  return useQuery<VoucherPrograms>({
    queryKey: ['voucher-programs'],
    queryFn: () =>
      api
        .get('/claims/voucher-programs/')
        .then((r) => r.data as VoucherPrograms),
    enabled,
  })
}

export type VoucherProgramInput = Partial<Omit<VoucherProgram, 'id'>>

export function useSaveVoucherProgram() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...body }: VoucherProgramInput & { id?: string }) =>
      (id
        ? api.put(`/claims/voucher-programs/${id}/`, body)
        : api.post('/claims/voucher-programs/', body)
      ).then((r) => r.data as VoucherProgram),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['voucher-programs'] }),
  })
}
